import { createRoutesFromElements, createBrowserRouter, Route, RouterProvider } from 'react-router-dom';
import { lazy, Suspense } from 'react';

import ErrorPage from './components/ErrorPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { OfflineBanner } from './components/OfflineFallback';

const DeviceListPage = lazy(() =>
  import('./components/DeviceListPage').then((m) => ({ default: m.DeviceListPage })),
);
const NotFoundPage = lazy(() =>
  import('./components/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);
const SignInPage = lazy(() =>
  import('./components/SignInPage').then((m) => ({ default: m.SignInPage })),
);
const SignUpPage = lazy(() =>
  import('./components/SignUpPage').then((m) => ({ default: m.SignUpPage })),
);
const SignedInLayout = lazy(() =>
  import('./components/SignedInLayout.tsx').then((m) => ({ default: m.SignedInLayout })),
);
const AuthCallbackPage = lazy(() =>
  import('./components/AuthCallbackPage.tsx').then((m) => ({ default: m.AuthCallbackPage })),
);
const SignOutPage = lazy(() =>
  import('./components/SignOutPage.tsx').then((m) => ({ default: m.SignOutPage })),
);
const EmailConfirmationPage = lazy(() =>
  import('./components/auth/EmailConfirmationPage.tsx').then((m) => ({ default: m.EmailConfirmationPage })),
);
const DeviceSetupPage = lazy(() =>
  import('./components/DeviceAuthPage').then((m) => ({ default: m.DeviceSetupPage })),
);
const DeviceAuthorizePage = lazy(() =>
  import('./components/DeviceAuthPage').then((m) => ({ default: m.DeviceAuthorizePage })),
);
const CLIActivatePage = lazy(() => import('./components/CLIActivatePage').then((m) => ({ default: m.CLIActivatePage })));
const DeviceLayout = lazy(() => import('./components/device/DeviceLayout').then((m) => ({ default: m.DeviceLayout })));
const DevicePage = lazy(() => import('./components/device/DevicePage').then((m) => ({ default: m.DevicePage })));
const InvitationAcceptPage = lazy(() =>
  import('./components/AcceptInvitationPage').then((m) => ({ default: m.InvitationAcceptPage })),
);

const SettingsLayout = lazy(() =>
  import('./components/device/settings/SettingsLayout.tsx').then((m) => ({ default: m.SettingsLayout })),
);
const GeneralSettingsPage = lazy(() =>
  import('./features/general-settings/GeneralSettingsPage').then((m) => ({ default: m.GeneralSettingsPage })),
);
const DeviceSharingPage = lazy(() =>
  import('./features/device-sharing/DeviceSharingPage').then((m) => ({ default: m.DeviceSharingPage })),
);
const FlightsPage = lazy(() => import('./features/flights/FlightsPage').then((m) => ({ default: m.FlightsPage })));
const FlightPage = lazy(() => import('./features/flights/FlightPage').then((m) => ({ default: m.FlightPage })));
const TestPlanPage = lazy(() =>
  import('./components/TestPlanPage').then((m) => ({ default: m.TestPlanPage })),
);
const OnboardingSurveyPage = lazy(() =>
  import('./components/OnboardingSurveyPage').then((m) => ({ default: m.OnboardingSurveyPage })),
);
const DeveloperPage = lazy(() =>
  import('./features/developer/DeveloperPage').then((m) => ({ default: m.DeveloperPage })),
);
const WatchPage = lazy(() => import('./features/watch/WatchPage').then((m) => ({ default: m.WatchPage })));
const DeviceWatchPage = lazy(() => import('./features/watch/DeviceWatchPage').then((m) => ({ default: m.DeviceWatchPage })));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" errorElement={<ErrorPage />}>
      <Route>
        <Route path="sign-in/*" element={<LazyRoute><SignInPage /></LazyRoute>} />
        <Route path="sign-up" element={<LazyRoute><SignUpPage /></LazyRoute>} />
        <Route path="sign-out" element={<LazyRoute><SignOutPage /></LazyRoute>} />
        <Route path="auth/callback" element={<LazyRoute><AuthCallbackPage /></LazyRoute>} />
        <Route path="verify-email" element={<LazyRoute><EmailConfirmationPage /></LazyRoute>} />
        <Route
          path="invitations/:invitationId/accept"
          element={
            <LazyRoute>
              <InvitationAcceptPage />
            </LazyRoute>
          }
        />
        <Route
          path="watch/:linkId"
          element={
            <LazyRoute>
              <WatchPage />
            </LazyRoute>
          }
        />
        <Route
          path="live/:device_id"
          element={
            <LazyRoute>
              <DeviceWatchPage />
            </LazyRoute>
          }
        />
      </Route>
      <Route element={<LazyRoute><SignedInLayout /></LazyRoute>}>
        <Route index element={<LazyRoute><DeviceListPage /></LazyRoute>} />
        <Route path="onboarding" element={<LazyRoute><OnboardingSurveyPage /></LazyRoute>} />
        <Route path="settings/developer" element={<LazyRoute><DeveloperPage /></LazyRoute>} />
        <Route
          path="device"
          element={
            <LazyRoute>
              <DeviceSetupPage />
            </LazyRoute>
          }
        />
        <Route
          path="device/authorize"
          element={
            <LazyRoute>
              <DeviceAuthorizePage />
            </LazyRoute>
          }
        />
        <Route
          path="activate"
          element={
            <LazyRoute>
              <CLIActivatePage />
            </LazyRoute>
          }
        />
        <Route
          path="devices/:device_id"
          element={
            <LazyRoute>
              <DeviceLayout />
            </LazyRoute>
          }
        >
          <Route
            index
            element={
              <LazyRoute>
                <DevicePage />
              </LazyRoute>
            }
          />
          <Route
            path="flights"
            element={
              <LazyRoute>
                <FlightsPage />
              </LazyRoute>
            }
          />
          <Route
            path="flights/:flight_id"
            element={
              <LazyRoute>
                <FlightPage />
              </LazyRoute>
            }
          />
          <Route
            element={
              <LazyRoute>
                <SettingsLayout />
              </LazyRoute>
            }
          >
            <Route
              path="settings/general"
              element={
                <LazyRoute>
                  <GeneralSettingsPage />
                </LazyRoute>
              }
            />
            <Route
              path="settings/sharing"
              element={
                <LazyRoute>
                  <DeviceSharingPage />
                </LazyRoute>
              }
            />
          </Route>
        </Route>
      </Route>
      <Route
          path="test-plan"
          element={
            <LazyRoute>
              <TestPlanPage />
            </LazyRoute>
          }
        />
        <Route path="*" element={<LazyRoute><NotFoundPage /></LazyRoute>} />
    </Route>,
  ),
);

export function App() {
  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
      <OfflineBanner />
    </ErrorBoundary>
  );
}
